// import { useRuntimeConfig } from '#app'
import { useI18n } from 'vue-i18n'
import { useRuntimeConfig } from '#imports'

const defaultState = {
  items: [],
  listItems: [],
  item: {},
  enums: {},
  pageCount: 0,
  total: 0,
  page: 1,
  loadingItems: false,
  loadingItem: false,
  error: null
}

const defaultActions = {
  async fetch(params = {}, isPrivate = false) {
    try {
      const config = useRuntimeConfig()

      this.loadingItems = true
      this.error = null

      const queryParams = new URLSearchParams()

      if (params.pLevel) queryParams.append('pLevel', params.pLevel)

      if (Array.isArray(params.populate) && params.populate.length) {
        params.populate.forEach((populate, index) => {
          queryParams.append(`populate[${index}]`, populate)
        })
      }

      if (params.sort) {
        queryParams.append('sort', params.sort)
      }

      if (Array.isArray(params.locale)) {
        params.locale.forEach((locale, index) => {
          queryParams.append(`filters[locale][$in][${index}]`, locale)
        })
      } else {
        queryParams.append('locale', params.locale)
      }

      if (params.page) queryParams.append('pagination[page]', params.page)
      if (params.pageSize) queryParams.append('pagination[pageSize]', params.pageSize)
      if (params.search) {
        queryParams.append(`filters[${params.search.field}][$containsi]`, params.search.value)
      }

      if (params.fields) {
        params.fields.forEach((field, index) => {
          queryParams.append(`fields[${index}]`, field)
        })
      }

      if (params.filters) {
        let andIndex = 0
        params.filters.forEach((filter) => {
          if (filter.$or) {
            filter.$or.forEach((subFilter, index) => {
              const fieldPath = subFilter.field
                .split('.')
                .reduce((acc, key) => `${acc}[${key}]`, `filters[$and][${andIndex}][$or][${index}]`)
              queryParams.append(`${fieldPath}[${subFilter.operator}]`, subFilter.value)
            })
            andIndex++
          } else {
            const fieldPath = filter.field.split('.').reduce((acc, key) => `${acc}[${key}]`, 'filters')

            // Handle $in operator with array values
            if (filter.operator === '$in' && Array.isArray(filter.value)) {
              filter.value.forEach((val, index) => {
                queryParams.append(`${fieldPath}[${filter.operator}][${index}]`, val)
              })
            } else {
              queryParams.append(`${fieldPath}[${filter.operator}]`, filter.value)
            }
          }
        })
      }

      const headers = {}

      let url = `${config.public.strapiApi}/${this.endpoint}?${queryParams.toString()}`

      if (params.populate && typeof params.populate === 'string') {
        url += `&${params.populate}`
      }

      // if (isPrivate) {
      //   const { useAuthStore } = await import('@/stores/auth.js')
      //   const authStore = useAuthStore()
      //   const userToken = authStore.token
      //   headers.Authorization = `Bearer ${userToken}`
      // }

      if (isPrivate) {
        const token = import.meta.client ? localStorage.getItem('authStore') : null
        if (token) {
          try {
            const authData = JSON.parse(token)
            if (authData.token) {
              headers.Authorization = `Bearer ${authData.token}`
            }
          } catch {
            console.warn('Failed to parse auth token from storage')
          }
        }
      }

      const response = await $fetch(url, { headers })

      if (params.list) {
        this.listItems = response.data
      } else if (params.infinite) {
        this.items = [...this.items, ...response.data]
        this.pageCount = response?.meta?.pagination?.pageCount
        this.total = response?.meta?.pagination?.total
        this.page = response?.meta?.pagination?.page
      } else {
        this.items = response.data
        this.pageCount = response?.meta?.pagination?.pageCount
        this.total = response?.meta?.pagination?.total
        this.page = response?.meta?.pagination?.page
      }
      this.loadingItems = false
      return response
    } catch (error) {
      console.error(error)
    } finally {
      this.loadingItems = false
    }
  },

  /**
   * Fetch a single item by ID.
   *
   * Pass the locale as a parameter if the function is not called at the top of a `setup` function.
   * It prevents `useI18n` from being called outside of setup and therefore throwing an error.
   *
   * @param {String | Number} id
   * @param {Array} populate
   * @param {String} language
   * @return {Promise<Object>}
   */
  async fetchOne(id, populate, language = null) {
    const config = useRuntimeConfig()

    this.loadingItem = true
    this.error = null

    if (!language) {
      const { locale } = useI18n()
      language = locale.value
    }

    try {
      const queryParams = new URLSearchParams()

      if (populate) {
        populate.forEach((pop, index) => {
          queryParams.append(`populate[${index}]`, pop)
        })
      } else {
        queryParams.append('pLevel', '3')
      }

      queryParams.append('locale', language)

      const item = await $fetch(`${config.public.strapiApi}/${this.endpoint}/${id}?${queryParams.toString()}`)
      this.item = item.data
      return item
    } catch (error) {
      this.error = error.message
      console.error(`Error fetching single item:`, error)
      this.item = {}
      throw error
    } finally {
      this.loadingItem = false
    }
  },

  async fetchOneBySlug(slug, locale = 'en', populate = 'pLevel=3', filters = '') {
    const config = useRuntimeConfig()
    const baseUrl = `${config.public.strapiApi}/${this.endpoint}`

    this.loadingItem = true
    this.error = null

    try {
      let slugQuery = `${baseUrl}?filters[slug][$eq]=${slug}&locale=*&pagination[pageSize]=1&fields=documentId`
      if (filters) {
        slugQuery += `&${filters}`
      }

      const slugItem = await $fetch(slugQuery)
      if (!slugItem.data?.length) {
        throw createError({ status: 404 })
      }

      const item = await $fetch(`${baseUrl}/${slugItem.data[0].documentId}?locale=${locale}&${populate}`)

      this.item = item.data
      return item
    } catch (error) {
      this.error = error.message
      this.item = {}

      if (error.status !== 404 && error.statusCode !== 404) {
        console.error(`Error fetching item by slug:`, error)
      } else {
        console.warn(`[404] ${this.endpoint} slug not found: ${slug}`)
      }

      throw error
    } finally {
      this.loadingItem = false
    }
  },

  async fetchOneByDocumentId(documentId, populate = 'pLevel=3') {
    const config = useRuntimeConfig()
    const { locale } = useI18n()
    this.loadingItem = true
    this.error = null

    try {
      const response = await $fetch(
        `${config.public.strapiApi}/${this.endpoint}?filters[documentId][$eq]=${documentId}&locale=${locale.value}&${populate}`
      )
      if (!response.data || response.data.length === 0) {
        throw new Error(`No item found with documentId ${documentId}`)
      }
      this.item = response.data[0]
      return response
    } catch (error) {
      this.error = error.message
      console.error(`Error fetching item by documentId:`, error)
      this.item = {}
      throw error
    } finally {
      this.loadingItem = false
    }
  },

  /**
   * Fetch a single item by its type.
   *
   * *Currently only used by the listing store.*
   *
   * @param {String} type
   * @param {String} populate
   * @param {Array} fields
   * @return {Promise<Object>}
   */
  async fetchOneByType(type, populate = undefined, fields = []) {
    const config = useRuntimeConfig()
    const { locale } = useI18n()
    this.loadingItem = true
    this.error = null
    let fetchPopulate = 'pLevel=3'
    if (populate) {
      fetchPopulate = populate
    }

    const fieldString = fields.reduce((acc, field, index) => {
      return `${acc}&fields[${index}]=${field}`
    }, '')

    try {
      const item = await $fetch(
        `${config.public.strapiApi}/listings?filters[type][$eq]=${type}&locale=${locale.value}&${fetchPopulate}${fieldString}`
      )
      if (!item.data || item.data.length === 0) {
        throw new Error(`No listing found with type ${type}`)
      }
      this.item = item.data[0]
      return item
    } catch (error) {
      this.error = error.message
      console.error(`Error fetching listing by type:`, error)
      this.item = {}
      throw error
    } finally {
      this.loadingItem = false
    }
  },

  async fetchBySugarId(sugarId, populate, localeParam = null) {
    const config = useRuntimeConfig()

    // Use provided locale parameter or fallback to useI18n()
    let locale
    if (localeParam) {
      locale = localeParam
    } else {
      const { locale: i18nLocale } = useI18n()
      locale = i18nLocale.value
    }

    this.loadingItem = true
    this.error = null

    const queryParams = new URLSearchParams()

    if (populate) {
      populate.forEach((pop, index) => {
        queryParams.append(`populate[${index}]`, pop)
      })
    } else {
      queryParams.append('pLevel', '3')
    }

    queryParams.append('locale', locale)

    try {
      const response = await $fetch(
        `${config.public.strapiApi}/${this.endpoint}?${queryParams.toString()}&filters[sugar_id][$eq]=${sugarId}`
      )

      if (response.data && response.data.length > 0) {
        return response.data[0]
      } else {
        throw new Error('Document not found')
      }
    } catch (error) {
      this.error = error.message
      console.error(`Error fetching document by sugar_id:`, error)
      throw error
    } finally {
      this.loadingItem = false
    }
  },

  async getEnums() {
    const config = useRuntimeConfig()
    try {
      this.enums = {}
      const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/get-enums`)
      this.enums = response
    } catch (error) {
      console.error(error)
    }
  },

  resetState() {
    this.items = []
    this.listItems = []
    this.item = {}
    this.pageCount = 0
    this.total = 0
    this.page = 1
    this.loadingItems = false
    this.loadingItem = false
    this.error = null
  },

  setMemberFilter(filters, value) {
    this.resetState()

    const filterParams = filters.filter(
      (filter) => !filter.$or || !filter.$or.some((f) => f.field === 'firstname' || f.field === 'lastname')
    )

    if (value) {
      const memberFilter = {
        $or: [
          { field: 'firstname', operator: '$containsi', value: value },
          { field: 'lastname', operator: '$containsi', value: value }
        ]
      }
      return [...filterParams, memberFilter]
    }

    return filterParams
  }
}

export { defaultState, defaultActions }
